/**
 * TextCell Component
 *
 * Renders text with inline editing support.
 * Single-line or multi-line based on settings.
 */

import { useState, useRef, useEffect } from 'react';
import type { TextCellProps } from './types';

export function TextCell({
  value,
  onChange,
  multiline = false,
  maxLength = 10000,
  readonly = false,
}: TextCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.text ?? '');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value?.text ?? '');
  }, [value?.text]);

  const handleSubmit = () => {
    setIsEditing(false);
    if (!onChange) return;

    const trimmed = editValue.trim();
    if (trimmed !== (value?.text ?? '')) {
      onChange(trimmed ? { text: trimmed } : null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value?.text ?? '');
    }
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center overflow-hidden">
        {value?.text ? (
          <span className="text-sm text-gray-700 truncate">{value.text}</span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  if (isEditing) {
    const inputProps = {
      ref: inputRef as React.RefObject<HTMLInputElement & HTMLTextAreaElement>,
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setEditValue(e.target.value.slice(0, maxLength)),
      onBlur: handleSubmit,
      onKeyDown: handleKeyDown,
      className: 'w-full text-sm bg-white border border-primary-500 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500',
    };

    if (multiline) {
      return (
        <textarea
          {...inputProps}
          rows={3}
          className={`${inputProps.className} resize-none`}
        />
      );
    }

    return <input type="text" {...inputProps} />;
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="w-full h-full flex items-center cursor-text overflow-hidden min-h-[28px]"
    >
      {value?.text ? (
        <span className="text-sm text-gray-700 truncate">{value.text}</span>
      ) : (
        <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
      )}
    </div>
  );
}
